import React, { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { EmptyTableMessage } from 'src/components/payments-ops/EmptyTableMessage'
import { GridTable } from 'src/components/shared/grid/GridTable'
import { PAYMENTS_OPS_PAGE_SIZE_OPTIONS } from 'src/constants/payments-ops/constants'
import { buildUsBankEarningColumns } from 'src/constants/payments-ops/usBankEarningsGrid'
import type { UsBankEarning } from 'src/interfaces/usBankTransactions'
import {
  getUsBankEarnings,
  getUsBankEarningsLoading,
  getUsBankEarningsPagination,
} from 'src/selectors/usBankTransactions'

interface Props {
  paginationModel: GridPaginationModel
  onPaginationModelChange(model: GridPaginationModel): void
  onShowOrders(earning: UsBankEarning): void
}

const getRowId = (row: UsBankEarning): string => {
  return row.earningId
}

const renderEmpty = () => {
  return <EmptyTableMessage message="No earnings match these filters." />
}

const UsBankEarningsTableComponent = ({
  paginationModel,
  onPaginationModelChange,
  onShowOrders,
}: Props) => {
  const earnings = useSelector(getUsBankEarnings)
  const pagination = useSelector(getUsBankEarningsPagination)
  const isLoading = useSelector(getUsBankEarningsLoading)

  const columns = useMemo(() => {
    return buildUsBankEarningColumns(onShowOrders)
  }, [onShowOrders])

  return (
    <GridTable<UsBankEarning>
      rows={earnings}
      columns={columns}
      getRowId={getRowId}
      isLoading={isLoading}
      fillHeight
      paginationMode="server"
      rowCount={pagination.total ?? 0}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      pageSizeOptions={PAYMENTS_OPS_PAGE_SIZE_OPTIONS}
      sortingMode="server"
      EmptyElement={renderEmpty}
      tableDataTestId="us-bank-earnings-table"
      rowDataTestIdPrefix="us-bank-earnings"
    />
  )
}

export const UsBankEarningsTable = memo(UsBankEarningsTableComponent)
